import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClub } from '@/contexts/ClubContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, Upload, Save, MapPin, Clock, Users, LayoutGrid, Plus, Trash2 } from 'lucide-react';
import { Club, PlayerSelectionMode, PlayTime } from '@/types';
import { billingService, clubService, uploadService } from '@/services/storageService';
import { API_BASE as API_BASE_URL } from '@/services/apiClient';
import { FormCardSkeleton, PageHeaderSkeleton } from '@/components/PageSkeletons';

const API_BASE = API_BASE_URL;

const normalizeLogoUrl = (raw?: string | null) => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // If backend returned an absolute URL with the wrong host/scheme, rebuild it against API_BASE
  const rebuild = (path: string) => {
    if (!API_BASE) return path;
    return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  try {
    const parsed = new URL(trimmed);
    const pathWithSearch = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return rebuild(pathWithSearch);
  } catch {
    return rebuild(trimmed);
  }
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_PLAY_TIME: PlayTime = { day: 'Monday', startTime: '18:00', endTime: '22:00' };

const SELECTION_MODES: { value: PlayerSelectionMode; label: string; description: string }[] = [
  { value: 'round-robin', label: 'Round Robin', description: 'Randomly picks 4 of the top 6, balances them into teams, and guarantees the remaining 2 are chosen next.' },
  { value: 'skill-based', label: 'Skill Based', description: 'Selects the top‑queued player, then picks others with the same skill (or closest skill if none), prioritizing higher wait time when multiple matches exist.' },
  { value: 'balanced', label: 'Balanced Teams', description: 'Takes the top two in queue, then matches skill to balance teams; if multiple combos fit, prioritizes longer wait time.' },
  { value: 'players-pick', label: 'Players Pick', description: 'Top queue player picks 3 teammates for the earliest free court; admin/coach can assist.' },
];

const mapSelectionMode = (mechanism?: string): Club['selectionMode'] => {
  if (!mechanism) return 'round-robin';
  switch (mechanism.toUpperCase()) {
    case 'SKILL_BASED':
      return 'skill-based';
    case 'BALANCED_TEAM':
      return 'balanced';
    case 'PLAYERS_PICK':
      return 'players-pick';
    default:
      return 'round-robin';
  }
};

const mapPlaySessions = (club: any): PlayTime[] => {
  if (!club?.play_sessions) return [];
  return club.play_sessions.map((session: any) => ({
    day: DAYS[Number(session.day_of_week || 1) - 1] || 'Monday',
    startTime: session.start_time_str,
    endTime: session.end_time_str,
  }));
};

const mapClubFromApi = (apiClub?: any): Club | null => {
  if (!apiClub) return null;
  const playTimes = mapPlaySessions(apiClub);
  return {
    id: String(apiClub.id),
    name: apiClub.name,
    sportType: apiClub.sport_type || undefined,
    slogan: apiClub.slogan || undefined,
    logo: normalizeLogoUrl(apiClub.logo_url),
    address: apiClub.address || undefined,
    playDays: playTimes.map((ps) => ps.day),
    playTimes,
    playTime: playTimes[0] ? `${playTimes[0].startTime} - ${playTimes[0].endTime}` : undefined,
    approxPlayers: apiClub.approx_num_players || undefined,
    numberOfCourts: apiClub.num_courts,
    selectionMode: mapSelectionMode(apiClub.selection_mechanism),
    allowDragSwap: apiClub.allow_drag_swap ?? true,
    allowQueueShuffle: apiClub.allow_queue_shuffle ?? true,
    adminId: apiClub.created_by_id ? String(apiClub.created_by_id) : undefined,
    createdAt: undefined,
  };
};

const buildClubPayload = (data: ReturnType<typeof buildFormState>) => {
  const mechanismMap: Record<string, string> = {
    'round-robin': 'ROUND_ROBIN',
    'skill-based': 'SKILL_BASED',
    'balanced': 'BALANCED_TEAM',
    'players-pick': 'PLAYERS_PICK',
  };

  return {
    name: data.name,
    sport_type: data.sportType || undefined,
    slogan: data.slogan || undefined,
    logo_url: data.logo || undefined,
    address: data.address || undefined,
    approx_players: data.approxPlayers,
    num_courts: data.numberOfCourts,
    selection_mechanism: mechanismMap[data.selectionMode] || 'ROUND_ROBIN',
    allow_drag_swap: data.allowDragSwap,
    allow_queue_shuffle: data.allowQueueShuffle,
    play_sessions: data.playTimes.map((ps) => ({
      day: DAYS.indexOf(ps.day) + 1 || 1,
      start_time: ps.startTime,
      end_time: ps.endTime,
    })),
  };
};

const buildFormState = (club?: Club | null) => {
  const playTimes = club?.playTimes?.length ? club.playTimes.map((pt) => ({ ...pt })) : [];
  const playDays = playTimes.length
    ? playTimes.map((pt) => pt.day)
    : Array.isArray(club?.playDays)
      ? [...club.playDays]
      : [];

  return {
    name: club?.name || '',
    sportType: club?.sportType || '',
    slogan: club?.slogan || '',
    logo: club?.logo || '',
    address: club?.address || '',
    playDays,
    playTime: club?.playTime || '',
    playTimes,
    approxPlayers: club?.approxPlayers ?? 20,
    numberOfCourts: club?.numberOfCourts ?? 4,
    selectionMode: (club?.selectionMode || 'round-robin') as PlayerSelectionMode,
    allowDragSwap: club?.allowDragSwap ?? true,
    allowQueueShuffle: club?.allowQueueShuffle ?? true,
  };
};

const isFormStateEqual = (a: ReturnType<typeof buildFormState>, b: ReturnType<typeof buildFormState>) => {
  if (a.name !== b.name) return false;
  if (a.sportType !== b.sportType) return false;
  if (a.slogan !== b.slogan) return false;
  if (a.logo !== b.logo) return false;
  if (a.address !== b.address) return false;
  if (a.playTime !== b.playTime) return false;
  if (a.approxPlayers !== b.approxPlayers) return false;
  if (a.numberOfCourts !== b.numberOfCourts) return false;
  if (a.selectionMode !== b.selectionMode) return false;
  if (a.allowDragSwap !== b.allowDragSwap) return false;
  if (a.allowQueueShuffle !== b.allowQueueShuffle) return false;
  if (a.playDays.length !== b.playDays.length) return false;
  if (a.playTimes.length !== b.playTimes.length) return false;
  if (a.playDays.some((day, idx) => day !== b.playDays[idx])) return false;
  for (let i = 0; i < a.playTimes.length; i += 1) {
    const left = a.playTimes[i];
    const right = b.playTimes[i];
    if (left.day !== right.day || left.startTime !== right.startTime || left.endTime !== right.endTime) {
      return false;
    }
  }
  return true;
};

export default function ClubManagement() {
  const { activeClubId, selectClub, currentClubRole } = useClub();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevClubIdRef = useRef<number | null>(null);
  const cropDragRef = useRef<{ startX: number; startY: number; startPos: { x: number; y: number } } | null>(null);

  const clubId = activeClubId ? Number(activeClubId) : null;
  const clubQuery = useQuery({
    queryKey: ['club', clubId],
    queryFn: () => clubService.get(clubId as number),
    enabled: Boolean(clubId),
  });

  const club = useMemo(() => mapClubFromApi(clubQuery.data), [clubQuery.data]);

  const [isEditing, setIsEditing] = useState(!club);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(buildFormState(club));
  const [newPlayTime, setNewPlayTime] = useState<PlayTime>(DEFAULT_PLAY_TIME);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const [cropBaseScale, setCropBaseScale] = useState(1);
  const [cropImgSize, setCropImgSize] = useState<{ w: number; h: number } | null>(null);
  const [cropFileName, setCropFileName] = useState<string>('club-logo.png');
  const [isCropping, setIsCropping] = useState(false);
  const [billing, setBilling] = useState<any>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const cropSize = 240;

  useEffect(() => {
    const clubChanged = clubId !== prevClubIdRef.current;
    if (clubChanged) {
      setFormData(buildFormState(club));
      // Only reset edit mode when the club changes, not on refetches.
      setIsEditing(!club);
      prevClubIdRef.current = clubId;
      return;
    }

    if (!isEditing) {
      const nextForm = buildFormState(club);
      setFormData((prev) => (isFormStateEqual(prev, nextForm) ? prev : nextForm));
    } else if (!club) {
      setIsEditing(true);
    }
  }, [club, clubId, isEditing]);

  useEffect(() => {
    if (!user || !clubId) return;
    setBillingLoading(true);
    billingService
      .getStatus(clubId)
      .then((res) => setBilling(res))
      .catch(() => setBilling(null))
      .finally(() => setBillingLoading(false));
  }, [user, clubId]);

  const canEdit =
    currentClubRole === 'admin' ||
    (club?.adminId && user?.id && String(club.adminId) === String(user.id));
  const isReadOnly = (!!club && !isEditing) || !canEdit;
  const normalizedPackageCode = String(billing?.package || billing?.recommended_package || 'package1')
    .trim()
    .toLowerCase();
  const isElitePackage = normalizedPackageCode === 'package3';
  const isLogoLockedByPackage = !isElitePackage;
  const visibleLogo = isElitePackage ? formData.logo : '';

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    if (isLogoLockedByPackage) {
      toast.error('Club logo is available on Elite (Package 3). Please upgrade your package.');
      e.target.value = '';
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file is too large (max 2MB)');
      e.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    setCropZoom(1);
    setCropPos({ x: 0, y: 0 });
    setCropImgSize(null);
    setCropBaseScale(1);
    setCropFileName(file.name || 'club-logo.png');
    setCropOpen(true);
    e.target.value = '';
  };

  const clampCropPos = (pos: { x: number; y: number }, zoom: number) => {
    if (!cropImgSize) return pos;
    const scaledW = cropImgSize.w * cropBaseScale * zoom;
    const scaledH = cropImgSize.h * cropBaseScale * zoom;
    const maxX = Math.max(0, (scaledW - cropSize) / 2);
    const maxY = Math.max(0, (scaledH - cropSize) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, pos.x)),
      y: Math.min(maxY, Math.max(-maxY, pos.y)),
    };
  };

  const handleCropZoomChange = (value: number) => {
    const nextZoom = Math.min(3, Math.max(1, value));
    setCropZoom(nextZoom);
    setCropPos((prev) => clampCropPos(prev, nextZoom));
  };

  const handleCropSave = async () => {
    if (!cropSrc || !cropImgSize) return;
    if (isCropping) return;
    if (isLogoLockedByPackage) {
      toast.error('Club logo is available on Elite (Package 3). Please upgrade your package.');
      return;
    }
    setIsCropping(true);
    try {
      const img = new Image();
      img.src = cropSrc;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvas = document.createElement('canvas');
      canvas.width = cropSize;
      canvas.height = cropSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not available');

      ctx.clearRect(0, 0, cropSize, cropSize);
      ctx.save();
      ctx.beginPath();
      ctx.arc(cropSize / 2, cropSize / 2, cropSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      const scaledW = cropImgSize.w * cropBaseScale * cropZoom;
      const scaledH = cropImgSize.h * cropBaseScale * cropZoom;
      const dx = cropSize / 2 - scaledW / 2 + cropPos.x;
      const dy = cropSize / 2 - scaledH / 2 + cropPos.y;
      ctx.drawImage(img, dx, dy, scaledW, scaledH);
      ctx.restore();

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to crop'))), 'image/png');
      });
      const baseName = cropFileName.replace(/\.[^.]+$/, '');
      const croppedFile = new File([blob], `${baseName}.png`, { type: 'image/png' });

      if (!clubId) throw new Error('Club context is required for logo uploads');
      const { url } = await uploadService.uploadClubLogo(croppedFile, clubId);
      const normalized = normalizeLogoUrl(url);
      setFormData((prev) => ({ ...prev, logo: normalized }));
      if (canEdit && clubId) {
        await clubService.update(clubId, { logo_url: normalized });
        await queryClient.invalidateQueries({ queryKey: ['club', clubId] });
      }
      toast.success('Logo uploaded');
      setCropOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload logo');
    } finally {
      setIsCropping(false);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
  };

  const handleCropPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cropImgSize) return;
    cropDragRef.current = { startX: e.clientX, startY: e.clientY, startPos: cropPos };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleCropPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cropDragRef.current) return;
    const { startX, startY, startPos } = cropDragRef.current;
    const next = { x: startPos.x + (e.clientX - startX), y: startPos.y + (e.clientY - startY) };
    setCropPos(clampCropPos(next, cropZoom));
  };

  const handleCropPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cropDragRef.current) return;
    cropDragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleDayToggle = (day: string) => {
    if (isReadOnly) return;

    setFormData((prev) => {
      const hasDay = prev.playDays.includes(day);
      if (hasDay) {
        const playDays = prev.playDays.filter((d) => d !== day);
        const playTimes = prev.playTimes.filter((pt) => pt.day !== day);
        return { ...prev, playDays, playTimes };
      }

      const playDays = [...prev.playDays, day];
      const playTimes = prev.playTimes.some((pt) => pt.day === day)
        ? prev.playTimes
        : [...prev.playTimes, { ...DEFAULT_PLAY_TIME, day }];
      return { ...prev, playDays, playTimes };
    });
  };

  const addPlayTime = () => {
    if (isReadOnly) return;

    if (!newPlayTime.day || !newPlayTime.startTime || !newPlayTime.endTime) {
      toast.error('Please fill in all play time fields');
      return;
    }
    setFormData((prev) => {
      const playTimes = [...prev.playTimes, { ...newPlayTime }];
      const playDays = Array.from(new Set([...prev.playDays, newPlayTime.day]));
      return { ...prev, playTimes, playDays };
    });
    setNewPlayTime(DEFAULT_PLAY_TIME);
  };

  const removePlayTime = (index: number) => {
    if (isReadOnly) return;

    setFormData((prev) => {
      const playTimes = prev.playTimes.filter((_, i) => i !== index);
      const remainingDays = playTimes.map((pt) => pt.day);
      return { ...prev, playTimes, playDays: remainingDays };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Club name is required');
      return;
    }
    if (!formData.sportType) {
      toast.error('Sport type is required');
      return;
    }

    if (!formData.playTimes.length) {
      toast.error('Add at least one scheduled play time');
      return;
    }

    if (!canEdit) {
      toast.error('You do not have permission to update the club');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildClubPayload({
        ...formData,
        name: formData.name.trim(),
        playDays: Array.from(new Set(formData.playTimes.map((pt) => pt.day))),
      });

      if (clubId) {
        await clubService.update(clubId, payload);
        await queryClient.invalidateQueries({ queryKey: ['club', clubId] });
        toast.success('Club updated successfully!');
        setIsEditing(false);
      } else {
        const res = await clubService.create(payload);
        const newClubId = res && typeof (res as any).club_id === 'number' ? (res as any).club_id : null;
        if (newClubId) {
          await selectClub(newClubId);
          await queryClient.invalidateQueries({ queryKey: ['club', newClubId] });
        }
        toast.success('Club created successfully!');
        setIsEditing(false);
      }
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to save club';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    try {
      const res = await billingService.createCheckoutSession({
        package: billing?.recommended_package || undefined,
        billing_cycle: billing?.recommended_billing_cycle || undefined,
      });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not start checkout');
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await billingService.createPortalSession();
      if (res?.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not open billing portal');
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancellingSubscription(true);
    try {
      const res = await billingService.cancelSubscription();
      if (res?.success) {
        toast.success('Subscription cancellation scheduled for period end.');
      }
      const latest = await billingService.getStatus(clubId);
      setBilling(latest);
    } catch (err: any) {
      toast.error(err?.message || 'Could not cancel subscription');
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  if (clubId && clubQuery.isLoading) {
    return (
      <div className="w-full space-y-6 sm:space-y-8 animate-fade-in px-3 sm:px-4 lg:px-6 overflow-x-hidden">
        <PageHeaderSkeleton />
        <FormCardSkeleton lines={8} />
        <FormCardSkeleton lines={6} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 sm:space-y-8 animate-fade-in px-3 sm:px-4 lg:px-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Building2 className="text-primary shrink-0" />
            <span className="truncate">
              {club ? (isEditing ? 'Club Settings' : 'Club Information') : 'Create Club'}
            </span>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            {club
              ? isEditing
                ? 'Edit your club details and settings'
                : 'View your club details'
              : 'Set up your badminton club to get started'}
            {!canEdit && (
              <span className="block text-xs text-destructive mt-1">
                You need admin access to make changes.
              </span>
            )}
          </p>
        </div>

        {club && canEdit && !isEditing && (
          <Button type="button" variant="outline" onClick={() => setIsEditing(true)} className="w-full sm:w-auto">
            Edit
          </Button>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 [&_label]:text-sm md:[&_label]:text-base [&_input]:h-10 md:[&_input]:h-11 [&_input]:text-sm md:[&_input]:text-base [&_textarea]:text-sm md:[&_textarea]:text-base"
      >
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl md:text-2xl">Basic Information</CardTitle>
            <CardDescription className="text-sm md:text-base">Your club&apos;s identity</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Logo row - responsive */}
            <div className="relative rounded-xl">
              <div
                className={`flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 transition-all ${
                  isLogoLockedByPackage ? 'opacity-100 pointer-events-none select-none' : ''
                }`}
              >
                <div
                  onClick={() => {
                    if (isReadOnly || isLogoLockedByPackage) return;
                    fileInputRef.current?.click();
                  }}
                  className={`w-24 h-24 sm:w-24 sm:h-24 rounded-full border-2 border-dashed border-border ${
                    isReadOnly || isLogoLockedByPackage ? '' : 'hover:border-primary cursor-pointer'
                  } transition-colors flex items-center justify-center bg-muted overflow-hidden shrink-0`}
                >
                  {visibleLogo ? (
                    <img src={visibleLogo} alt="Club logo" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="text-muted-foreground" size={24} />
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={isReadOnly || isLogoLockedByPackage}
                />

                <div className="min-w-0">
                  <p className="font-medium">Club Logo</p>
                  <p className="text-sm text-muted-foreground">
                    {isReadOnly ? 'Logo is locked until you edit the club' : 'Click to upload an image'}
                  </p>
                </div>
              </div>

              {isLogoLockedByPackage && (
                <div className="absolute inset-0 rounded-xl border border-dashed border-red-500/70 bg-background/20 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="text-center max-w-sm">
                    <p className="text-sm sm:text-base font-semibold text-foreground">Club logo is an Elite feature</p>
                    <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                      Upgrade to Package 3 (Elite) to upload and manage your club logo.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Name + sport + slogan */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Club Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Smash Masters Club"
                  required
                  disabled={isReadOnly || isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sportType">Which sport does your club play? *</Label>
                <select
                  id="sportType"
                  value={formData.sportType}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sportType: e.target.value }))}
                  required
                  className="h-10 md:h-11 w-full px-3 rounded-md border border-input bg-background text-sm md:text-base"
                  disabled={isReadOnly || isSubmitting}
                >
                  <option value="">Select sport</option>
                  <option value="Badminton">Badminton</option>
                  <option value="Pickleball">Pickleball</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slogan">Slogan</Label>
                <Input
                  id="slogan"
                  value={formData.slogan}
                  onChange={(e) => setFormData((prev) => ({ ...prev, slogan: e.target.value }))}
                  placeholder="Play hard, smash harder!"
                  disabled={isReadOnly || isSubmitting}
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin size={16} /> Address
              </Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="123 Sports Center, City"
                rows={2}
                disabled={isReadOnly || isSubmitting}
              />
            </div>
          </CardContent>
        </Card>

        {/* Play Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                  <Clock size={20} /> Play Schedule
                </CardTitle>
                <CardDescription className="text-sm md:text-base">Configure your weekly play schedule</CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Days */}
                <div className="space-y-3">
                  <Label>Play Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((day) => (
                      <label
                        key={day}
                        className={`px-3 sm:px-4 py-2 rounded-lg border cursor-pointer transition-all text-sm sm:text-base ${
                          formData.playDays.includes(day)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:border-primary'
                        }`}
                      >
                        <Checkbox
                          checked={formData.playDays.includes(day)}
                          onCheckedChange={() => handleDayToggle(day)}
                          className="sr-only"
                          disabled={isReadOnly || isSubmitting}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Default Play Time */}
                <div className="space-y-2">
                  <Label htmlFor="playTime">Default Play Time</Label>
                  <Input
                    id="playTime"
                    value={formData.playTime}
                    onChange={(e) => setFormData((prev) => ({ ...prev, playTime: e.target.value }))}
                    placeholder="6:00 PM - 10:00 PM"
                    disabled={isReadOnly || isSubmitting}
                  />
                </div>

                {/* Scheduled Play Times */}
                <div className="space-y-4">
                  <Label>Scheduled Play Times</Label>

                  {formData.playTimes.length > 0 && (
                    <div className="space-y-2">
                      {formData.playTimes.map((pt, index) => (
                        <div
                          key={index}
                          className="p-3 bg-muted rounded-lg flex flex-col sm:flex-row sm:items-center gap-2"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{pt.day}</span>
                            <span className="text-muted-foreground">-</span>
                            <span className="text-sm">
                              {pt.startTime} - {pt.endTime}
                            </span>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="sm:ml-auto h-8 w-8 self-end sm:self-auto"
                            onClick={() => removePlayTime(index)}
                            disabled={isReadOnly || isSubmitting}
                          >
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add play time - responsive grid (same inputs) */}
                  <div className="p-4 border border-dashed rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:items-end">
                      <div className="space-y-1">
                        <Label className="text-sm">Day</Label>
                        <select
                          value={newPlayTime.day}
                          onChange={(e) => setNewPlayTime((prev) => ({ ...prev, day: e.target.value }))}
                          className="h-10 md:h-11 w-full px-3 rounded-md border border-input bg-background text-sm md:text-base"
                          disabled={isReadOnly || isSubmitting}
                        >
                          {DAYS.map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">Start</Label>
                        <Input
                          type="time"
                          value={newPlayTime.startTime}
                          onChange={(e) => setNewPlayTime((prev) => ({ ...prev, startTime: e.target.value }))}
                          disabled={isReadOnly || isSubmitting}
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">End</Label>
                        <Input
                          type="time"
                          value={newPlayTime.endTime}
                          onChange={(e) => setNewPlayTime((prev) => ({ ...prev, endTime: e.target.value }))}
                          disabled={isReadOnly || isSubmitting}
                        />
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addPlayTime}
                        disabled={isReadOnly || isSubmitting}
                        className="w-full"
                      >
                        <Plus size={16} className="mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Capacity Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                  <Users size={20} /> Capacity Settings
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="approxPlayers">Approximate Players</Label>
                    <Input
                      id="approxPlayers"
                      type="number"
                      min="4"
                      value={formData.approxPlayers}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          approxPlayers: parseInt(e.target.value, 10) || 0,
                        }))
                      }
                      disabled={isReadOnly || isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="numberOfCourts">Number of Courts</Label>
                    <Input
                      id="numberOfCourts"
                      type="number"
                      min="1"
                      max="20"
                      value={formData.numberOfCourts}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          numberOfCourts: parseInt(e.target.value, 10) || 1,
                        }))
                      }
                      disabled={isReadOnly || isSubmitting}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Player Selection Mode */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                  <LayoutGrid size={20} /> Player Selection Mode
                </CardTitle>
                <CardDescription className="text-sm md:text-base">How players are assigned to courts</CardDescription>
              </CardHeader>

              <CardContent>
                <RadioGroup
                  value={formData.selectionMode}
                  onValueChange={(v) => {
                    if (isReadOnly) return;
                    setFormData((prev) => ({ ...prev, selectionMode: v as PlayerSelectionMode }));
                  }}
                  className="space-y-3"
                >
                  {SELECTION_MODES.map((mode) => (
                    <label
                      key={mode.value}
                      className={`flex items-start p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.selectionMode === mode.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <RadioGroupItem value={mode.value} className="mt-1" />
                      <div className="ml-3">
                        <p className="font-medium">{mode.label}</p>
                        <p className="text-sm md:text-base text-muted-foreground">{mode.description}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Queue & Court Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                  <LayoutGrid size={20} /> Queue & Court Controls
                </CardTitle>
                <CardDescription className="text-sm md:text-base">Enable or disable drag swap and queue shuffle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label>Allow Swap of players once assigned to a court</Label>
                  <RadioGroup
                    value={formData.allowDragSwap ? 'enabled' : 'disabled'}
                    onValueChange={(v) => {
                      if (isReadOnly) return;
                      setFormData((prev) => ({ ...prev, allowDragSwap: v === 'enabled' }));
                    }}
                    className="flex flex-col gap-2"
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="enabled" />
                      <span>Enabled</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="disabled" />
                      <span>Disabled</span>
                    </label>
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Allow suffle of the queue (Please Note it will reset wait time in queue and shuffle players position in queue)</Label>
                  <RadioGroup
                    value={formData.allowQueueShuffle ? 'enabled' : 'disabled'}
                    onValueChange={(v) => {
                      if (isReadOnly) return;
                      setFormData((prev) => ({ ...prev, allowQueueShuffle: v === 'enabled' }));
                    }}
                    className="flex flex-col gap-2"
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="enabled" />
                      <span>Enabled</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="disabled" />
                      <span>Disabled</span>
                    </label>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl">Billing & Subscription</CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Manage your Stripe subscription, invoices, and payment method securely.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {billingLoading ? (
                  <p className="text-muted-foreground">Loading billing status...</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-md border p-3">
                        <p className="text-sm text-muted-foreground">Current status</p>
                        <p className="text-base md:text-lg font-semibold">{billing?.status || 'Not subscribed'}</p>
                      </div>
                      <div className="rounded-md border p-3">
                        <p className="text-sm text-muted-foreground">Current plan</p>
                        <p className="text-base md:text-lg font-semibold">
                          {billing?.package || billing?.recommended_package || 'package1'} ({billing?.billing_cycle || billing?.recommended_billing_cycle || 'monthly'})
                        </p>
                      </div>
                      <div className="rounded-md border p-3">
                        <p className="text-sm text-muted-foreground">Trial ends</p>
                        <p className="text-base md:text-lg font-semibold">
                          {billing?.trial_end ? new Date(billing.trial_end).toLocaleDateString() : '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button type="button" onClick={handleCheckout}>Start / Update Checkout</Button>
                      <Button type="button" variant="outline" onClick={handleManageBilling}>
                        Manage Billing
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleCancelSubscription}
                        disabled={
                          isCancellingSubscription ||
                          !billing?.stripe_subscription_id ||
                          Boolean(billing?.cancel_at_period_end)
                        }
                      >
                        {billing?.cancel_at_period_end
                          ? 'Cancellation Scheduled'
                          : isCancellingSubscription
                            ? 'Cancelling...'
                            : 'Cancel Subscription'}
                      </Button>
                    </div>

                    {billing?.cancel_at_period_end && (
                      <p className="text-sm text-muted-foreground">
                        Your subscription will end on{' '}
                        {billing?.current_period_end
                          ? new Date(billing.current_period_end).toLocaleDateString()
                          : 'the current period end date'}
                        .
                      </p>
                    )}

                    {Array.isArray(billing?.invoices) && billing.invoices.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-base md:text-lg">Recent invoices</Label>
                        <div className="space-y-2">
                          {billing.invoices.slice(0, 5).map((inv: any) => (
                            <div key={inv.stripe_invoice_id} className="rounded-md border p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium">{inv.stripe_invoice_id}</p>
                                <p className="text-xs text-muted-foreground">
                                  {inv.status || 'unknown'} • {inv.currency?.toUpperCase() || 'CAD'} {typeof inv.amount_paid === 'number' ? (inv.amount_paid / 100).toFixed(2) : '-'}
                                </p>
                              </div>
                              {inv.hosted_invoice_url ? (
                                <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                                  View invoice
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

        {/* Submit */}
        {(!club || isEditing) && (
          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || isReadOnly}>
            <Save size={20} className="mr-2" />
            {isSubmitting ? 'Saving...' : club ? 'Save Changes' : 'Create Club'}
          </Button>
        )}
      </form>

      <Dialog
        open={cropOpen}
        onOpenChange={(open) => {
          if (!open && cropSrc) {
            URL.revokeObjectURL(cropSrc);
            setCropSrc(null);
          }
          setCropOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crop Club Logo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-center">
              <div
                className="relative rounded-full overflow-hidden border bg-muted cursor-grab active:cursor-grabbing"
                style={{ width: cropSize, height: cropSize }}
                onPointerDown={handleCropPointerDown}
                onPointerMove={handleCropPointerMove}
                onPointerUp={handleCropPointerUp}
              >
                {cropSrc && (
                  <img
                    src={cropSrc}
                    alt="Crop preview"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      const baseScale = Math.max(cropSize / img.naturalWidth, cropSize / img.naturalHeight);
                      setCropImgSize({ w: img.naturalWidth, h: img.naturalHeight });
                      setCropBaseScale(baseScale);
                      setCropPos({ x: 0, y: 0 });
                    }}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: cropImgSize ? cropImgSize.w : 'auto',
                      height: cropImgSize ? cropImgSize.h : 'auto',
                      transform: `translate(-50%, -50%) translate(${cropPos.x}px, ${cropPos.y}px) scale(${cropBaseScale * cropZoom})`,
                      transformOrigin: 'center',
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Zoom</Label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={cropZoom}
                onChange={(e) => handleCropZoomChange(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCropOpen(false)}
                disabled={isCropping}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleCropSave} disabled={isCropping || !cropSrc}>
                {isCropping ? 'Saving...' : 'Save Logo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
